import { createMemo } from 'solid-js';
import ItemStackView from './ItemStackView';
import SearchableItemPicker, { Highlight } from './SearchableItemPicker';
import { useTranslation } from '../hooks/useTranslation';
import { useItemName } from '../hooks/useItemName';
import { fromItemStackDto } from '../services/itemRefAdapters';
import { formatQuantity } from '../services/quantityFormatter';
import { itemKey, parseItemKey } from '../services/craftingResolver';
import { useCraftingResolver } from '../services/craftingResolverContext';
import type { ItemStackDto } from '../services/inventoryApi';
import styles from './StorageSelect.module.css';

/** Picks one item out of a storage's stock, as the storage items endpoint lists it. */
export default function StorageItemPicker(props: {
    stacks: ItemStackDto[];
    value: string;
    onChange: (itemKey: string) => void;
    placeholder: string;
    disabled?: boolean;
}) {
    const { t } = useTranslation();
    const { itemName, stackSize } = useItemName();
    const resolver = useCraftingResolver();

    const nameOf = (stack: ItemStackDto) => itemName(stack.key, stack.snbt);
    // The chosen item stays a choice when the storage no longer lists it, so editing a list a run sold out keeps it.
    const unlistedValue = createMemo<ItemStackDto[]>(() => {
        const parsed = props.value ? parseItemKey(props.value) : null;
        if (!parsed || props.stacks.some(stack => stack.key === props.value)) return [];
        return [{ key: props.value, itemId: parsed.id, metadata: parsed.metadata, count: 0, isCustom: parsed.hash != null }];
    });
    const stacksByName = createMemo(() =>
        [...props.stacks, ...unlistedValue()].sort((left, right) => nameOf(left).localeCompare(nameOf(right))));

    return (
        <SearchableItemPicker
            choices={stacksByName()}
            keyOf={stack => stack.key}
            searchTextOf={stack => `${nameOf(stack)} ${stack.key} ${resolver()?.searchText(itemKey(stack.itemId, stack.metadata)) ?? ''}`}
            value={props.value}
            onChange={props.onChange}
            placeholder={stacksByName().length === 0 ? t('STORAGE_ITEM_PICKER_EMPTY') : props.placeholder}
            searchPlaceholder={t('STORAGE_ITEM_PICKER_SEARCH')}
            noMatchKey="STORAGE_ITEM_PICKER_NO_MATCH"
            disabled={props.disabled}
            renderSelected={stack => (
                <>
                    <ItemStackView stack={{ ...fromItemStackDto(stack), count: 1 }} size={22} bare />
                    <span class={styles.name}>{nameOf(stack)}</span>
                </>
            )}
            renderOption={(stack, query) => (
                <>
                    <span class={styles.lead}><ItemStackView stack={{ ...fromItemStackDto(stack), count: 1 }} size={24} bare /></span>
                    <span class={styles.body}>
                        <span class={styles.lineTop}>
                            <span class={styles.name}><Highlight text={nameOf(stack)} query={query()} /></span>
                            <span class={styles.stats}>{formatQuantity(stack.count, stackSize(stack.key))}</span>
                        </span>
                    </span>
                </>
            )}
        />
    );
}
